import React, { useState, Profiler, useEffect } from "react";
import { useRouter } from "next/router";
import { DataTable } from "@abyss/web/ui/DataTable";
import { columns } from "./migrationSchedulerColumnDefinition";
import { FormProvider } from "@abyss/web/ui/FormProvider";
import { useForm } from "@abyss/web/hooks/useForm";
import { Grid } from "@abyss/web/ui/Grid";
import { Flex } from "@abyss/web/ui/Flex";
import { Heading } from "@abyss/web/ui/Heading";
import { TextInput } from "@abyss/web/ui/TextInput";
import { NumberInput } from "@abyss/web/ui/NumberInput";
import { Button } from "@abyss/web/ui/Button";
import { Layout } from "@abyss/web/ui/Layout";
import { IconSymbol } from "@abyss/web/ui/IconSymbol";
import { DeviceMigrationSchedulerService } from "@src/api/web-services/demo/pages/workspace/DeviceMigration";
import { useGridBuilder } from "@src/components/global/v1/GridBuilder";
import { BreadcrumbService } from "@src/api/common/BreadcrumbService";
import { PageTitleService } from "@src/api/common/PageTitleService";
import { Breadcrumbs, Title } from "./PageInfo";

function onRenderCallback(
  id, // "Grid"
  phase, // "mount" or "update"
  actualDuration // time spent rendering
) {
  console.log(`[${id}] ${phase} rendered in ${actualDuration} ms`);
}

// ── Table component using useGridBuilder ───────────────────────────────
export const MigrationSchedulerGrid = () => {
  const router = useRouter();
  const [isPersistenceEnabled, setIsPersistenceEnabled] = useState(true);
  const [isDebugEnabled, setIsDebugEnabled] = useState(false);
  const { UpdateBreadcrumb } = BreadcrumbService();
  const { UpdatePageTitle } = PageTitleService();

  const { GetDeviceMigrationWorkspaceScheduler } =
    DeviceMigrationSchedulerService();

  const form = useForm({
    defaultValues: {
      name: "",
      businessId: 6,
    },
  });

  const DataSource = {
    GridApiMethod: async () => {
      const body = form.getValues();
      const apiData = await GetDeviceMigrationWorkspaceScheduler(body);
      console.log("API Response:", apiData);
      return apiData.data?.results || [];
    },
  };

  const Action = {
    Refresh: async () => {
      const data = await DataSource.GridApiMethod();
      tableState.setData(data);
    },
    Init: async () => {
      await UpdateBreadcrumb(Breadcrumbs);
      await UpdatePageTitle(Title);
    },
  };

  useEffect(() => {
        Action.Init();
      }, []);

  const { tableState, clearPreference, resetFormValues, onRefresh } =
    useGridBuilder({
      columns,
      form,
      onRefresh: () => Action.Refresh(),
      dataTableOptions: {
        showPagination: true,
        pageSizeOptions: [5, 10, 15, 25, 50, 100],
        showDownloadButton: true,
        showGlobalFilter: true,
        showFilterDataset: true,
        showTableSettings: true,
        showColumnVisibilityConfig: true,
      },
      persistence: {
        enabled: isPersistenceEnabled,
        clientId: 5,
        storageName: "device-migration-scheduler-grid",
        debug: isDebugEnabled,
      },
    });

  return (
    <div>
      <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            gap: "12px",
            alignItems: "center",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={isPersistenceEnabled}
              onChange={() => setIsPersistenceEnabled((previous) => !previous)}
            />
            GridStatePersistence {isPersistenceEnabled ? "Enabled" : "Disabled"}
          </label>
          <span style={{ fontSize: "0.85em", color: "#666" }}>
            Filter, sort, or change pages then reload — your state is restored
            from localStorage
          </span>
          <button
            type="button"
            onClick={clearPreference}
            style={{
              padding: "4px 12px",
              cursor: "pointer",
              borderRadius: "4px",
              border: "1px solid #ccc",
              background: "#f5f5f5",
            }}
          >
            Clear Preference
          </button>
        </div>
        <div
          style={{
            marginBottom: "16px",
            display: "flex",
            gap: "12px",
            alignItems: "center",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={isDebugEnabled}
              onChange={() => setIsDebugEnabled((previous) => !previous)}
            />
            GridDebug {isDebugEnabled ? "Enabled" : "Disabled"}
          </label>
          <span style={{ fontSize: "0.85em", color: "#666" }}>
            When enabled, diagnostic messages are logged to the browser console
            for persist, restore, and clear operations
          </span>
        </div>

        <FormProvider state={form}>
          <Grid>
            <Grid.Col span={{ xs: "100%" }}>
              <Grid>
                <Grid.Col span={{ xs: 12 }}>
                  <Flex justify="space-between">
                    <Heading display="h4" color="$primary1">
                      Filters
                    </Heading>
                  </Flex>
                </Grid.Col>
                <Grid.Col span={{ xs: 12, md: 4 }}>
                  <TextInput label="Name" model="name" />
                </Grid.Col>
                <Grid.Col span={{ xs: 12, md: 4 }}>
                  <NumberInput label="Business Id" model="businessId" />
                </Grid.Col>
                <Grid.Col span={{ xs: 12 }}>
                  <Layout.Group>
                    <Button
                      type="submit"
                      onClick={() => {
                        onRefresh();
                      }}
                    >
                      Filter
                    </Button>
                    <Button
                      size="$md"
                      variant="outline"
                      onClick={() => {
                        resetFormValues();
                      }}
                    >
                      Reset
                    </Button>
                  </Layout.Group>
                </Grid.Col>
              </Grid>
            </Grid.Col>
          </Grid>
        </FormProvider>

        <div style={{ overflowX: "auto", width: "100%" }}>
          <Profiler id="Grid" onRender={onRenderCallback}>
            <DataTable
              title="Migration Scheduler"
              hideTitleHeader
              tableState={tableState}
            />
          </Profiler>
        </div>
      </div>
    </div>
  );
}
